<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from "vue";
import api from "@/services/api";

/**
 * Props:
 * - plan (obj com price, planId, etc.)
 * - user (obj com email...)
 * - couponCode (string) - ✅ MUDANÇA: Novo campo
 * - finalPrice (number) - ✅ MUDANÇA: Novo campo para valor com desconto
 *
 * Emite:
 * - subscription-result: { success: boolean, message: string }
 * - loading-change: boolean  // true = processando, false = liberado
 */
const props = defineProps({
  plan: { type: Object, required: true },
  user: { type: Object, required: true },
  couponCode: { type: String, default: "" }, // ✅ Recebido via SubscriptionForm
  finalPrice: { type: Number, default: null }, // ✅ Recebido via SubscriptionForm
});

const emit = defineEmits(["subscription-result", "loading-change"]);

const isLoading = ref(false);
const isPolling = ref(false);
const error = ref(null);

const brickInstance = ref(null);
const pollingTimeoutId = ref(null);

// ✅ MUDANÇA: Helper para limpar a instância do Mercado Pago com segurança
const unmountBrick = async () => {
  if (brickInstance.value) {
    await brickInstance.value.unmount();
    brickInstance.value = null;
  }
};

const clearPoll = () => {
  if (pollingTimeoutId.value) {
    clearTimeout(pollingTimeoutId.value);
    pollingTimeoutId.value = null;
  }
};

const formatCurrency = (value) => {
  return Number(value ?? 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const buildCardOneTimeResultPayload = (
  apiResult = {},
  fallbackSuccess = true
) => {
  const status = (apiResult?.status || "").toLowerCase();

  const creditAmount = Number(apiResult?.creditAmount ?? 0);
  const originalAmount = Number(
    apiResult?.originalAmount ?? props.plan?.price ?? 0
  );
  const chargedAmount = Number(apiResult?.chargedAmount ?? 0);

  const hasProportionalCredit = creditAmount > 0;

  const isFreeUpgrade =
    apiResult?.isFreeUpgrade === true || status === "free_upgrade";

  let message = "Você tem acesso ao plano por 30 dias.";

  if (hasProportionalCredit && isFreeUpgrade) {
    const creditCoveredExactly = creditAmount >= originalAmount;

    const coverageMessage = creditCoveredExactly
      ? "O crédito disponível na sua conta cobriu integralmente o valor desta assinatura."
      : "O crédito disponível na sua conta cobriu o valor desta assinatura, e o saldo restante era inferior a R$ 0,50 — valor abaixo do mínimo aceito para cobrança.";

    message = [
      "Plano atualizado com sucesso.",
      "",
      coverageMessage,
      "Por isso, nenhum pagamento foi processado e sua nova assinatura já está ativa.",
      "",
      `Valor da assinatura: ${formatCurrency(originalAmount)}`,
      `Crédito aplicado: ${formatCurrency(creditAmount)}`,
      `Valor pago agora: ${formatCurrency(0)}`,
    ].join("\n");
  } else if (hasProportionalCredit) {
    message = [
      "Plano atualizado com sucesso.",
      "",
      "Parte do valor da sua assinatura anterior foi aproveitada neste pagamento.",
      "",
      `Valor da assinatura: ${formatCurrency(originalAmount)}`,
      `Crédito aplicado: ${formatCurrency(creditAmount)}`,
      `Valor pago agora: ${formatCurrency(chargedAmount)}`,
    ].join("\n");
  }

  return {
    success: fallbackSuccess,
    ...apiResult,
    isFreeUpgrade,
    hasProportionalCredit,
    creditAmount,
    originalAmount,
    chargedAmount,
    planId: apiResult?.planId ?? props.plan?.planId ?? null,
    planName: apiResult?.planName ?? props.plan?.planName ?? props.plan?.name ?? null,
    value: chargedAmount || originalAmount || props.plan?.price || null,
    message,
  };
};

const startPolling = (subscriptionId, apiResult = {}) => {
  isPolling.value = true;
  isLoading.value = false;
  emit("loading-change", false);
  clearPoll();

  const poll = async () => {
    try {
      const { data } = await api.get(`/payment/status/${subscriptionId}`);
      const status = (data?.status || "").toLowerCase();
      const paymentStatus = (data?.paymentStatus || "").toLowerCase();

      if (status === "active" || paymentStatus === "approved") {
        isPolling.value = false;
        error.value = null;

        emit(
          "subscription-result",
          buildCardOneTimeResultPayload(apiResult, true)
        );

        return;
      }

      if (paymentStatus === "rejected" || paymentStatus === "cancelled") {
        isPolling.value = false;

        const msg =
          "O pagamento foi rejeitado/cancelado. Verifique os dados do cartão ou tente outro meio de pagamento.";

        error.value = msg;

        emit("subscription-result", {
          success: false,
          ...apiResult,
          message: msg,
        });

        return;
      }

      pollingTimeoutId.value = setTimeout(poll, 5000);
    } catch (err) {
      console.error("(Cartão - OneTime) Erro polling:", err);
      isPolling.value = false;

      const msg = "Erro ao verificar o status do pagamento.";
      error.value = msg;

      emit("subscription-result", {
        success: false,
        ...apiResult,
        message: msg,
      });
    }
  };

  poll();
};

const initializeBrick = async () => {
  try {
    if (!window.MercadoPago) {
      error.value = "SDK do Mercado Pago não foi carregada.";
      return;
    }

    // ✅ MUDANÇA: Garante limpeza antes de (re)inicializar
    await unmountBrick();

    const YOUR_PUBLIC_KEY = process.env.VUE_APP_MP_PUBLIC_KEY;
    const mp = new window.MercadoPago(YOUR_PUBLIC_KEY, { locale: "pt-BR" });
    const bricksBuilder = mp.bricks();

    // ✅ MUDANÇA: O valor deve ser o com desconto (finalPrice) ou o original do plano
    const amountToPay =
      props.finalPrice !== null ? props.finalPrice : Number(props.plan.price);

    brickInstance.value = await bricksBuilder.create(
      "cardPayment",
      "card-payment-onetime-container",
      {
        initialization: {
          amount: amountToPay,
          payer: { email: props.user.email },
        },
        customization: { visual: { style: { theme: "default" } } },
        callbacks: {
          onReady: () =>
            console.log(
              "Brick Cartão (OneTime) pronto com valor:",
              amountToPay
            ),

          onSubmit: async (formData) => {
            // ==========================================================
            // ✅ MUDANÇA: trava duplo clique / re-submit durante a requisição
            // (antes você não tinha hasSubmitted aqui)
            // ==========================================================
            if (isLoading.value) return;

            let shouldRecreateBrick = true;

            isLoading.value = true;
            emit("loading-change", true);
            error.value = null;

            try {
              const payerEmail =
                formData?.payer?.email || props.user?.email || "";
              const idType =
                formData?.payer?.identification?.type?.trim() || "CPF";
              const idNumber =
                formData?.payer?.identification?.number?.trim() || "";

              if (!payerEmail)
                throw new Error("E-mail do pagador é obrigatório.");
              if (!idNumber) throw new Error("CPF do pagador é obrigatório.");

              const payload = {
                planId: Number(props.plan.planId),
                couponCode: props.couponCode,
                token: formData?.token,
                paymentMethodId: formData?.payment_method_id,
                issuerId: formData?.issuer_id,
                installments: formData?.installments,
                payer: {
                  email: payerEmail,
                  identification: {
                    type: idType,
                    number: idNumber,
                  },
                },
              };

              if (!payload.token) {
                throw new Error("Token do cartão não retornado pelo Brick.");
              }

              const { data } = await api.post(
                "/payment/create-card-payment",
                payload
              );

              const mpStatus = (data?.status || "").toLowerCase();

              const subscriptionIdRaw =
                data?.externalReference || data?.newSubscriptionId;

              const localSubscriptionId = subscriptionIdRaw
                ? Number(subscriptionIdRaw)
                : null;

              const isFreeUpgrade =
                data?.isFreeUpgrade === true || mpStatus === "free_upgrade";

              if (!localSubscriptionId || Number.isNaN(localSubscriptionId)) {
                throw new Error("API não retornou um SubscriptionId válido.");
              }

              // ✅ Desmonta antes de seguir para resultado/polling
              await unmountBrick();

              // ✅ Crédito proporcional cobriu tudo.
              // Não há pagamento real no Mercado Pago e não deve haver polling.
              if (isFreeUpgrade) {
                shouldRecreateBrick = false;

                emit(
                  "subscription-result",
                  buildCardOneTimeResultPayload(data, true)
                );

                return;
              }

              if (mpStatus === "approved") {
                shouldRecreateBrick = false;

                emit(
                  "subscription-result",
                  buildCardOneTimeResultPayload(data, true)
                );

                return;
              }

              if (mpStatus === "rejected" || mpStatus === "cancelled") {
                emit("subscription-result", {
                  success: false,
                  message:
                    "O pagamento foi rejeitado/cancelado. Verifique os dados do cartão.",
                  status: mpStatus,
                });
                return;
              }

              startPolling(localSubscriptionId, data);
            } catch (err) {
              console.error("[CardOneTimeBrick] Erro no onSubmit:", err);

              const errorData = err?.response?.data;

              // ✅ MUDANÇA: seu backend às vezes devolve { error: "..." }
              const errorMessage =
                errorData?.error ||
                errorData?.message ||
                err?.message ||
                "Falha ao criar pagamento.";

              error.value = errorMessage;

              emit("subscription-result", {
                success: false,
                message: errorMessage,
                status: "error",
              });
            } finally {
              // ==========================================================
              // ✅ MUDANÇA CRÍTICA:
              // token do cartão é single-use. Após qualquer submit (ok/erro),
              // recria o Brick para garantir token novo no próximo PAGAR.
              // ==========================================================
              isLoading.value = false;
              emit("loading-change", false);

              // Se estiver em polling, não recrie o brick (o usuário não deve pagar de novo)
              if (shouldRecreateBrick && !isPolling.value) {
                await nextTick();
                await unmountBrick();
                await initializeBrick();
              }
            }
          },

          onError: (err) => {
            isLoading.value = false;
            emit("loading-change", false);
            emit("subscription-result", {
              success: false,
              message: err?.message || "Erro no Brick Cartão.",
              status: "error",
            });
          },
        },
      }
    );
  } catch (e) {
    console.error("Falha ao inicializar Brick Cartão:", e);
  }
};

/**
 * ✅ MUDANÇA: Observa se o preço final mudou (cupom aplicado/removido)
 * e reinicializa o Brick para o Mercado Pago recalcular as parcelas e valor.
 */
watch(
  () => props.finalPrice,
  async () => {
    // ✅ MUDANÇA: não reinicia brick durante submit/polling
    if (isLoading.value || isPolling.value) return;

    await nextTick();
    await initializeBrick();
  }
);

onMounted(async () => {
  if (props.plan && props.user) {
    await nextTick();
    await initializeBrick();
  }
});

onUnmounted(() => {
  unmountBrick();
  clearPoll();
});
</script>

<template>
  <div>
    <div id="card-payment-onetime-container" v-show="!isPolling"></div>
  </div>
</template>